import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IconId, Page, PageKeys, TransformedPage } from './type';
import { RouteConfig } from '../../pages/Router';
import { Translation } from '../language/translationType';
import { SteeringWheelSvg } from '../../styles/icons/SteeringWheelSvg';
import { UserSvg } from '../../styles/icons/UserSvg';
import { TicketSvg } from '../../styles/icons/TicketSvg';
import { SignpostTemplate, SignpostTemplateType } from '../../pages/templates/signpost/SignpostTemplate';
import { DisplaySvg } from '../../styles/icons/DisplaySvg';
import { MapSvg } from '../../styles/icons/MapSvg';
import {
  TransportationTypesTemplate,
  TransportationTypesTemplateType
} from '../../pages/templates/transportationTypes/TransportationTypesTemplate';
import { RsPinTemplate, RsPinTemplateType } from '../../pages/templates/rsPin/rsPinTemplate';
import {
  PhoneNumberCheckTemplate,
  PhoneNumberCheckTemplateType
} from '../../pages/templates/phoneNumberCheck/phoneNumberCheckTemplate';
import { MainFormTemplate, MainFormTemplateType } from '../../pages/templates/mainForm/mainFormTemplate';
import { RegistrationTemplate, RegistrationTemplateType } from '../../pages/templates/registration/registrationTemplate';
import { UnloadingSignpostTemplate } from '../../pages/templates/unloadingSignpost/UnloadingSignpostTemplate';
import {
  InfoForDriverTemplate,
  InfoForDriverTemplateType
} from '../../pages/templates/infoForDriver/InfoForDriverTemplate';
import { TextListTemplate, TextListTemplateType } from '../../pages/templates/textList/textListTemplate';
import { UniversalTemplate, UniversalTemplateType } from '../../pages/templates/universal/universalTemplate';
import { useLocation } from 'react-router-dom';
import { toggleHeaderMenu } from '../../store/slices/app/appSlice';
import {
  SearchTransportationTemplate,
  SearchTransportationTemplateType
} from '../../pages/templates/searchTransportation/SearchTransportationTemplate';
import { MapTemplate, MapTemplateType } from '../../pages/templates/map/MapTemplate';
import {
  CurrentStatusTemplate,
  CurrentStatusTemplateType
} from '../../pages/templates/currentStatus/CurrentStatusTemplate';

type Container = {
  icons: Record<string, React.ReactElement>
  png: Record<string, string>
  templates: Record<string, (page: Page<any>) => React.ReactElement>
}

export const elementsContainer: Container = {
  icons: {
    steeringWheel: <SteeringWheelSvg width="65%" />,
    user: <UserSvg width="65%" />,
    ticket: <TicketSvg width="45%" />,
    display: <DisplaySvg width="65%" />,
    map: <MapSvg width="65%" />,
  },
  png: {
    map: 'map.png',
    mobile: 'mobil.png',
    // missing
    pin: 'pin.png',
    truck: 'truck.png',
  },
  templates: {
    signpost: (page: Page<SignpostTemplateType>) => {
      return <SignpostTemplate page={page} />
    },
    transportationTypes: (page: Page<TransportationTypesTemplateType>) => {
      return <TransportationTypesTemplate page={page} />
    },
    rsPin: (page: Page<RsPinTemplateType>) => {
      return <RsPinTemplate page={page} />
    },
    searchTransportation: (page: Page<SearchTransportationTemplateType>) => {
      return <SearchTransportationTemplate page={page} />
    },
    phoneNumberCheck: (page: Page<PhoneNumberCheckTemplateType>) => {
      return <PhoneNumberCheckTemplate page={page} />
    },
    mainForm: (page: Page<MainFormTemplateType>) => {
      return <MainFormTemplate page={page} />
    },
    registration: (page: Page<RegistrationTemplateType>) => {
      return <RegistrationTemplate page={page} />
    },
    unloadingSignpost: (page: Page<SignpostTemplateType>) => {
      return <UnloadingSignpostTemplate page={page} />
    },
    infoForDriver: (page: Page<InfoForDriverTemplateType>) => {
      return <InfoForDriverTemplate page={page} />
    },
    textList: (page: Page<TextListTemplateType>) => {
      return <TextListTemplate page={page} />
    },
    universal: (page: Page<UniversalTemplateType>) => {
      return <UniversalTemplate page={page} />
    },
    map: (page: Page<MapTemplateType>) => {
      return <MapTemplate page={page} />
    },
    currentStatus: (page: Page<CurrentStatusTemplateType>) => {
      return <CurrentStatusTemplate page={page} />
    },
    /*
    systemEntry: (page: Page<React.ReactElement>) => {
      return <SystemEntryTemplate page={page} />
    },
    driverEntry: (page: Page<React.ReactElement>) => {
      return <DriverEntryTemplate page={page} />
    },
    currentStatus: (page: Page<React.ReactElement>) => {
      return <CurrentStatusTemplate page={page} />
    },
    overallOverview: (page: Page<React.ReactElement>) => {
      return <OverallOverviewTemplate page={page} />
    },
     */
  },
}

export const getIcon = (iconId: IconId): React.ReactElement | null => {
  if (!(iconId in elementsContainer.icons)) {
    return null;
  }

  return elementsContainer.icons[iconId]
}

const HeaderConfig = (props: { page: TransformedPage }): React.ReactElement => {
  const location = useLocation()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(toggleHeaderMenu(props.page.header === undefined || !!props.page.header.menu))
    document.getElementById('main-content')?.scrollTo(0, 0)
  }, [location.key]);

  return <></>
}


const getTemplate = (page: TransformedPage): React.ReactElement | null => {
  if (!(page.templateId in elementsContainer.templates)) {
    return null;
  }

  return (
    <>
      <HeaderConfig page={page} />
      <>
        {elementsContainer.templates[page.templateId](page)}
      </>
    </>
  )
}

export const transformPage = (page: Page<IconId>, translation: Translation): TransformedPage => {
  const transformedPage = page as unknown as TransformedPage

  const pageElement = getTemplate(transformedPage as TransformedPage)

  return {
    ...transformedPage,
    template: pageElement ?? <></>,
  }
}

export const getRoutes = (pages: Record<PageKeys, Page<IconId>>, translation: Translation): RouteConfig[] => {
  return Object.values(pages).map(page => {
    const transformedPage = transformPage(page, translation)

    return {
      path: transformedPage.urlPath,
      component: transformedPage.template,
    }
  })
}
